import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Users,
  Wallet,
  Banknote,
  BarChart3,
  Dumbbell,
  ChevronLeft,
  ChevronRight,
  X,
  Printer,
  Maximize2,
  Check,
  Clock,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Phone,
  Mail,
  Globe,
} from "lucide-react";

const NAVY = "#1B365D";
const ORANGE = "#DA291C";

const Logo = ({ className = "h-10 w-auto" }) => (
  <img src="/logo.png" alt="PBStudio" className={className} />
);

const SlideShell = ({ children, badge, className = "" }) => (
  <div className={`h-full w-full flex flex-col px-12 py-10 ${className}`}>
    {badge && (
      <div className="mb-6">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold uppercase tracking-wider border border-red-100">
          <Sparkles className="w-3.5 h-3.5" /> {badge}
        </span>
      </div>
    )}
    {children}
  </div>
);

// ───── Slide 1: Kapak ──────────────────────────────────────────
const SlideCover = () => (
  <SlideShell>
    <div className="flex items-center justify-between mb-8">
      <Logo className="h-12 w-auto" />
      <span className="text-xs font-semibold text-grey-600 uppercase tracking-[0.2em]">
        Spor Stüdyosu Yönetim Platformu
      </span>
    </div>

    <div className="flex-1 flex flex-col justify-center max-w-5xl">
      <h1
        className="font-heading font-extrabold leading-[0.95] mb-6"
        style={{ color: NAVY, fontSize: "clamp(2.5rem, 5.5vw, 5rem)" }}
      >
        Stüdyonuzun her şeyi.<br />
        <span style={{ color: ORANGE }}>Tek bir yerde.</span>
      </h1>
      <p className="text-xl text-grey-700 mb-10 max-w-3xl leading-relaxed">
        Rezervasyondan paket satışına, antrenör maaşından detaylı finansal raporlara kadar
        spor stüdyonuzu uçtan uca dijitalleştirin. Sade, mobil uyumlu ve çok dilli.
      </p>

      <div className="grid grid-cols-3 gap-6 max-w-3xl">
        {[
          { v: "5 dk", l: "Kurulum süresi" },
          { v: "Çok dilli", l: "Birden fazla dil desteği" },
          { v: "7/24", l: "Bulut tabanlı" },
        ].map((s, i) => (
          <div key={i} className="border-l-4 pl-4" style={{ borderColor: ORANGE }}>
            <div className="font-heading font-extrabold text-3xl" style={{ color: NAVY }}>
              {s.v}
            </div>
            <div className="text-sm text-grey-600 mt-1">{s.l}</div>
          </div>
        ))}
      </div>
    </div>

    <div className="flex items-center gap-3 mt-8 text-sm text-grey-500">
      <ShieldCheck className="w-4 h-4" /> Yurt içinde ve yurt dışında stüdyolar tek panelde
    </div>
  </SlideShell>
);

// ───── Slide 2: Problem & Çözüm ────────────────────────────────
const SlideProblem = () => (
  <SlideShell badge="Sorun & Çözüm">
    <h2 className="font-heading font-extrabold text-5xl leading-tight mb-2" style={{ color: NAVY }}>
      Excel ve WhatsApp ile <span style={{ color: ORANGE }}>yetinmek zorunda değilsiniz.</span>
    </h2>
    <p className="text-lg text-grey-600 mb-10 max-w-3xl">
      Her gün karşılaştığınız 3 büyük baş ağrısı — ve PBStudio çözümü.
    </p>

    <div className="grid grid-cols-3 gap-6 flex-1">
      {[
        {
          problem: "Kim, ne zaman, hangi ders?",
          solution: "Akıllı Rezervasyon",
          desc: "Online ders rezervasyonu, otomatik hatırlatma, çakışma kontrolü.",
          icon: Calendar,
        },
        {
          problem: "Ay sonu hesap karmaşası",
          solution: "Otomatik Prim & Maaş",
          desc: "Antrenör primi otomatik hesaplanır. Maaş, kira, aidat tek panelde.",
          icon: Wallet,
        },
        {
          problem: "Net kâr-zarar belirsiz",
          solution: "Detaylı Finans Raporu",
          desc: "Ödenen / Bekleyen ayrımıyla tam şeffaf finansal rapor.",
          icon: BarChart3,
        },
      ].map((item, i) => {
        const Icon = item.icon;
        return (
          <div
            key={i}
            className="bg-white rounded-2xl border border-grey-100 p-6 flex flex-col shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-2">
              ✗ Sorun
            </div>
            <p className="text-grey-700 mb-5 leading-snug">{item.problem}</p>

            <div className="flex items-center gap-3 mb-3 mt-auto">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: `${ORANGE}15` }}
              >
                <Icon className="w-6 h-6" style={{ color: ORANGE }} />
              </div>
              <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: ORANGE }}>
                ✓ Çözüm
              </div>
            </div>
            <h3 className="font-heading font-bold text-xl mb-2" style={{ color: NAVY }}>
              {item.solution}
            </h3>
            <p className="text-sm text-grey-600 leading-relaxed">{item.desc}</p>
          </div>
        );
      })}
    </div>
  </SlideShell>
);

// ───── Slide 3: Ana Özellikler ─────────────────────────────────
const features = [
  {
    icon: Calendar,
    title: "Rezervasyon Sistemi",
    items: ["Online ders rezervasyonu", "Çakışma & kapasite kontrolü", "Otomatik iptal politikası"],
  },
  {
    icon: Users,
    title: "Üye Yönetimi",
    items: ["Üye profilleri & ölçümler", "Paket & seans takibi", "Mobil üye paneli"],
  },
  {
    icon: Wallet,
    title: "Paket & Ödeme",
    items: ["Çoklu ödeme yöntemi", "Taksit & vade takibi", "Otomatik makbuz"],
  },
  {
    icon: Banknote,
    title: "Maaş & Prim",
    items: ["Otomatik prim hesabı", "Aylık maaş tahakkuku", "Toplu ödeme desteği"],
  },
  {
    icon: BarChart3,
    title: "Detaylı Raporlar",
    items: ["Ödenen / Bekleyen ayrımı", "Excel & PDF dışa aktarım", "Tarih aralığı filtresi"],
  },
  {
    icon: Dumbbell,
    title: "Antrenör & Şube",
    items: ["Çoklu şube yönetimi", "Antrenör çalışma takvimi", "Performans analizi"],
  },
];

const SlideFeatures = () => (
  <SlideShell badge="Ana Özellikler">
    <h2 className="font-heading font-extrabold text-5xl leading-tight mb-2" style={{ color: NAVY }}>
      Bir stüdyo için <span style={{ color: ORANGE }}>ihtiyacınız olan her şey.</span>
    </h2>
    <p className="text-lg text-grey-600 mb-8">
      6 modül, tek panel. Hepsi birbirine entegre, hepsi sade.
    </p>

    <div className="grid grid-cols-3 gap-5 flex-1">
      {features.map((f, i) => {
        const Icon = f.icon;
        return (
          <div
            key={i}
            className="bg-white rounded-2xl border border-grey-100 p-5 flex flex-col hover:border-red-200 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: NAVY }}
              >
                <Icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-heading font-bold text-lg leading-tight" style={{ color: NAVY }}>
                {f.title}
              </h3>
            </div>
            <ul className="space-y-1.5 mt-1">
              {f.items.map((it, j) => (
                <li key={j} className="flex items-start gap-2 text-sm text-grey-700">
                  <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: ORANGE }} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  </SlideShell>
);

// ───── Slide 4: Ekran Görüntüleri (gerçek arayüz) ──────────────
const SlideScreens = () => (
  <SlideShell badge="Ekran Görüntüleri">
    <h2 className="font-heading font-extrabold text-5xl leading-tight mb-2" style={{ color: NAVY }}>
      Sade arayüz, <span style={{ color: ORANGE }}>güçlü altyapı.</span>
    </h2>
    <p className="text-lg text-grey-600 mb-6 max-w-3xl">
      Her ekranda sade bir tasarım, her veride mutlak şeffaflık.
    </p>

    <div className="flex-1 grid grid-cols-2 gap-5">
      {/* Real Reports screenshot */}
      <div className="relative rounded-2xl border border-grey-200 bg-white shadow-xl overflow-hidden flex flex-col">
        <div className="px-3 py-2 bg-grey-50 border-b border-grey-100 flex items-center justify-between">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          </div>
          <span className="text-[11px] font-semibold text-grey-600">Detaylı Finans Raporu</span>
          <span />
        </div>
        <div className="flex-1 overflow-hidden bg-white">
          <img
            src="/screenshots/presentation/reports.png"
            alt="Raporlar Modülü"
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>

      {/* Real Members screenshot */}
      <div className="relative rounded-2xl border border-grey-200 bg-white shadow-xl overflow-hidden flex flex-col">
        <div className="px-3 py-2 bg-grey-50 border-b border-grey-100 flex items-center justify-between">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          </div>
          <span className="text-[11px] font-semibold text-grey-600">Üye Yönetimi</span>
          <span />
        </div>
        <div className="flex-1 overflow-hidden bg-white">
          <img
            src="/screenshots/presentation/members.png"
            alt="Üye Yönetimi Modülü"
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>

      {/* Real Reservations screenshot */}
      <div className="relative rounded-2xl border border-grey-200 bg-white shadow-xl overflow-hidden flex flex-col">
        <div className="px-3 py-2 bg-grey-50 border-b border-grey-100 flex items-center justify-between">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          </div>
          <span className="text-[11px] font-semibold text-grey-600">Rezervasyon Takvimi</span>
          <span />
        </div>
        <div className="flex-1 overflow-hidden bg-white">
          <img
            src="/screenshots/presentation/reservations.png"
            alt="Rezervasyon Modülü"
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>

      {/* Real Dashboard screenshot */}
      <div className="relative rounded-2xl border border-grey-200 bg-white shadow-xl overflow-hidden flex flex-col">
        <div className="px-3 py-2 bg-grey-50 border-b border-grey-100 flex items-center justify-between">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          </div>
          <span className="text-[11px] font-semibold text-grey-600">Genel Bakış</span>
          <span />
        </div>
        <div className="flex-1 overflow-hidden bg-white">
          <img
            src="/screenshots/presentation/dashboard.png"
            alt="Dashboard"
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>
    </div>
  </SlideShell>
);

// ───── Slide 5: Avantajlar / Rakamlar ──────────────────────────
// ───── Slide 5: Neden PBStudio? ────────────────────────────────
const SlideBenefits = () => (
  <SlideShell badge="Neden PBStudio?">
    <h2 className="font-heading font-extrabold text-5xl leading-tight mb-2" style={{ color: NAVY }}>
      Genel bir SaaS değil, <span style={{ color: ORANGE }}>spor stüdyosu için tasarlandı.</span>
    </h2>
    <p className="text-lg text-grey-600 mb-8 max-w-4xl">
      Gerçek stüdyo akışlarına göre kurgulandı. Yurt içi ve yurt dışı stüdyolar tek panelde yönetilir; sahibinin merak ettiği her veri tek tıkta.
    </p>

    <div className="grid grid-cols-3 gap-4 flex-1">
      {[
        {
          icon: Globe,
          title: "Çok dilli & küresel",
          desc: "Birden fazla dil ve dinamik dil yönetimi. TL, USD, EUR, GBP — istediğiniz para birimi. Yurt içinde ve yurt dışında çalışır.",
        },
        {
          icon: BarChart3,
          title: "Şeffaf finans modeli",
          desc: "Ödenen, bekleyen ve toplam ayrı ayrı görünür. Kira, aidat, maaş, prim, paket satışı — her kalem için vade takibi.",
        },
        {
          icon: Users,
          title: "3 rol, 3 ayrı panel",
          desc: "İşletme sahibi, antrenör ve üye için ayrı arayüzler. Herkes sadece kendine ait olanı görür, yetkiler net.",
        },
        {
          icon: Banknote,
          title: "Otomatik prim & maaş",
          desc: "Antrenörün her dersi sistemde işlenir, primi otomatik hesaplanır. Kira, prim, maaş — tek tıkla tahsil ya da öde.",
        },
        {
          icon: Calendar,
          title: "Üye self-servis",
          desc: "Üyeler kendi seansını kendi alır, iptal eder, paketini görür. Geç iptal politikasını siz belirlersiniz, sistem uygular.",
        },
        {
          icon: ShieldCheck,
          title: "Tek panelde her şey",
          desc: "Üye, rezervasyon, paket, ödeme, maaş, raporlar, çoklu şube — hepsi entegre. Excel + WhatsApp + ayrı muhasebe yazılımına gerek yok.",
        },
      ].map((item, i) => {
        const Icon = item.icon;
        return (
          <div
            key={i}
            className="bg-white rounded-2xl border border-grey-100 p-5 hover:border-red-200 hover:shadow-md transition flex flex-col"
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${ORANGE}15` }}
              >
                <Icon className="w-5 h-5" style={{ color: ORANGE }} />
              </div>
              <h3 className="font-heading font-bold text-base leading-tight" style={{ color: NAVY }}>
                {item.title}
              </h3>
            </div>
            <p className="text-sm text-grey-600 leading-relaxed">{item.desc}</p>
          </div>
        );
      })}
    </div>
  </SlideShell>
);

// ───── Slide 7: CTA / İletişim ─────────────────────────────────
const SlideCTA = ({ onStart }) => (
  <div className="h-full w-full flex flex-col px-12 py-10 relative overflow-hidden text-white" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0E1F3D 60%, ${NAVY} 100%)` }}>
    <div
      className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full opacity-20 pointer-events-none"
      style={{ background: ORANGE, filter: "blur(120px)" }}
    />

    <div className="relative flex-1 flex flex-col justify-center max-w-5xl">
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur text-white text-xs font-semibold uppercase tracking-[0.2em] mb-8 self-start border border-white/15">
        <Sparkles className="w-3.5 h-3.5" /> Hemen Başlayın
      </div>
      <h2 className="font-heading font-extrabold text-white leading-[0.95] mb-6" style={{ fontSize: "clamp(3rem, 6vw, 5.5rem)" }}>
        Stüdyonuzu <br />
        <span style={{ color: ORANGE }}>dijitalleştirmenin</span> <br />
        zamanı geldi.
      </h2>
      <p className="text-xl text-grey-300 mb-10 max-w-3xl leading-relaxed">
        14 gün ücretsiz deneyin. Kredi kartı istemiyoruz. Destek ekibimiz kurulumda yanınızda.
      </p>

      <div className="flex flex-wrap gap-4 mb-12">
        <button
          onClick={onStart}
          className="px-8 py-4 rounded-xl text-white font-bold text-lg flex items-center gap-2 hover:scale-105 transition-transform shadow-2xl"
          style={{ background: ORANGE }}
          data-testid="presentation-cta-start"
        >
          Hemen Başla <ArrowRight className="w-5 h-5" />
        </button>
        <button
          onClick={() => (window.location.href = "mailto:info@pbstudio.com")}
          className="px-8 py-4 rounded-xl bg-white/10 backdrop-blur text-white font-semibold text-lg flex items-center gap-2 hover:bg-white/20 transition border border-white/20"
        >
          <Mail className="w-5 h-5" /> Demo Talep Et
        </button>
      </div>

      <div className="flex flex-wrap gap-8 text-grey-300 text-sm">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4" /> +90 533 430 83 28
        </div>
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4" /> info@pbstudio.com
        </div>
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4" /> pbstudio.com
        </div>
      </div>
    </div>

    <div className="relative mt-auto flex items-center justify-between pt-6 border-t border-white/10">
      <Logo className="h-8 w-auto opacity-90" />
      <div className="text-xs text-grey-400">© 2026 PBStudio · pbstudio.com</div>
    </div>
  </div>
);

// ───── Ana Sayfa ───────────────────────────────────────────────
const slides = [SlideCover, SlideProblem, SlideFeatures, SlideScreens, SlideBenefits, SlideCTA];
const slideTitles = ["Kapak", "Sorun & Çözüm", "Özellikler", "Ekran Görüntüleri", "Avantajlar", "İletişim"];

export default function PresentationPage() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => setCurrent((c) => Math.min(c + 1, slides.length - 1)), []);
  const prev = useCallback(() => setCurrent((c) => Math.max(c - 1, 0)), []);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        prev();
      } else if (e.key === "Home") {
        setCurrent(0);
      } else if (e.key === "End") {
        setCurrent(slides.length - 1);
      } else if (e.key === "Escape") {
        if (document.fullscreenElement) document.exitFullscreen();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [next, prev]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const handlePrint = () => window.print();

  const SlideComp = slides[current];

  return (
    <div className="min-h-screen bg-slate-100 font-body text-grey-900 print:bg-white">
      {/* Top bar - non-print */}
      <div className="print:hidden sticky top-0 z-40 bg-white border-b border-grey-100 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo className="h-7 w-auto" />
          <span className="text-sm font-semibold" style={{ color: NAVY }}>
            Sunum Modu
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-grey-700 hover:bg-grey-100 flex items-center gap-1.5"
            data-testid="presentation-print-btn"
            title="Yazdır / PDF olarak kaydet"
          >
            <Printer className="w-4 h-4" /> PDF
          </button>
          <button
            onClick={toggleFullscreen}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-grey-700 hover:bg-grey-100 flex items-center gap-1.5"
            data-testid="presentation-fullscreen-btn"
            title="Tam ekran"
          >
            <Maximize2 className="w-4 h-4" /> Tam Ekran
          </button>
          <button
            onClick={() => navigate("/")}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-grey-700 hover:bg-grey-100 flex items-center gap-1.5"
            data-testid="presentation-close-btn"
          >
            <X className="w-4 h-4" /> Kapat
          </button>
        </div>
      </div>

      {/* Slide canvas - 16:9 aspect, looks like PowerPoint */}
      <div className="flex items-center justify-center p-4 sm:p-8 print:p-0">
        <div
          className="relative w-full max-w-[1280px] aspect-[16/9] bg-white rounded-2xl shadow-2xl overflow-hidden print:shadow-none print:rounded-none print:max-w-none print:aspect-auto print:h-screen"
          data-testid="presentation-slide-canvas"
        >
          <SlideComp onStart={() => navigate("/auth?mode=register")} />

          {/* Slide number badge */}
          <div className="print:hidden absolute bottom-4 right-4 px-3 py-1 rounded-full bg-white/80 backdrop-blur text-xs font-semibold text-grey-700 border border-grey-200">
            {current + 1} / {slides.length}
          </div>
        </div>
      </div>

      {/* Bottom navigator - non-print */}
      <div className="print:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur border-t border-grey-100 px-6 py-3 flex items-center justify-between gap-4">
        <button
          onClick={prev}
          disabled={current === 0}
          className="px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-1.5 disabled:opacity-30 hover:bg-grey-100 transition"
          data-testid="presentation-prev-btn"
        >
          <ChevronLeft className="w-4 h-4" /> Önceki
        </button>

        <div className="flex-1 flex items-center justify-center gap-2 overflow-x-auto max-w-2xl">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`group flex flex-col items-center gap-1 transition ${
                i === current ? "scale-110" : "opacity-50 hover:opacity-100"
              }`}
              data-testid={`presentation-dot-${i}`}
              title={slideTitles[i]}
            >
              <div
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === current ? "32px" : "16px",
                  background: i === current ? ORANGE : "#C3C4C6",
                }}
              />
              <span className="text-[10px] text-grey-500 hidden sm:block whitespace-nowrap">{slideTitles[i]}</span>
            </button>
          ))}
        </div>

        <button
          onClick={next}
          disabled={current === slides.length - 1}
          className="px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-1.5 disabled:opacity-30 text-white hover:opacity-90 transition"
          style={{ background: NAVY }}
          data-testid="presentation-next-btn"
        >
          Sonraki <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: landscape; margin: 0; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>
  );
}
